/**
 * Box 6, Budget pacing (DEC5 spec §4): this calendar month (Bangkok) against
 * the approved plan, in one currency. Pure: no I/O, no i18n. It ignores the
 * page's date and channel filters; only the page currency reaches it (R3).
 *
 * A missing plan is `noPlan`, never 0%. Spend of an account with no known
 * currency is left out of every currency and flagged (R4); spend in another
 * currency is never added. The status always comes from `pacingOf`, never
 * from a rounded percentage (m4).
 *
 * @module services/looker/budgetPacingData
 */
import { daysInMonthOf, monthStartOf, shiftDataDate } from '@/services/dataDate';
import {
  TARGET_PLATFORMS, pacingOf, planFor, type Pacing, type PacingStatus, type PlanRow, type TargetPlatform,
} from '@/services/targets/targetRules';
import { platformLabel } from '@/utils/platformLabel';
import { numCell, tableOf, type AppLabelKey, type AppTileData, type Cell, type LabelFn } from './lookerAppData';

/** One row of `get_month_spend` (migration 119). currency null = the account's currency is unknown. */
export interface MonthSpendRow {
  platform: string;
  currency: string | null;
  spend: number;
  dataThrough: string | null;
}

export interface BudgetPacingChannel {
  platform: string;
  /** null = spend but no channel plan. */
  plan: number | null;
  spend: number;
  pacing: Pacing | null;
}

export type BudgetPacingView =
  | { kind: 'noPlan'; month: string; currency: string | null; reason: 'none' | 'pickCurrency' }
  | {
      kind: 'ready'; month: string; currency: string; plan: number; planDerived: boolean;
      mismatch: { channelSum: number; total: number } | null;
      spend: number; days: number; daysInMonth: number; dataThrough: string | null; pacing: Pacing;
      channels: BudgetPacingChannel[];
      unknownCurrencyExcluded: boolean;
    };

/** What the chat is told about box 6 (spec §4 "Chat and CSV"). */
export type BudgetPacingChat =
  | { status: 'noPlan'; reason: 'none' | 'pickCurrency'; currency: string | null }
  | {
      month: string; currency: string; spend: number; plan: number; planDerived: boolean;
      pacingPct: number | null; status: PacingStatus; projected: number | null;
      dataThrough: string | null; days: number; daysInMonth: number; unknownCurrencyExcluded: boolean;
      channels: Array<{ platform: string; plan: number | null; spend: number; pacingPct: number | null; status: PacingStatus | null }>;
    };

const STATUS_WORD: Record<PacingStatus, AppLabelKey> = {
  under: 'pacingUnder', onTrack: 'pacingOnTrack', over: 'pacingOver', tooEarly: 'pacingTooEarly',
};

/** A projection from the first 1–2 days is noise: shown as "—" wherever the % is withheld (M3). */
export const shownProjection = (p: Pacing): number | null => (p.status === 'tooEarly' ? null : p.projected);

const dayOfMonth = (iso: string): number => Number(iso.slice(8, 10));
const sum = (ns: readonly number[]): number => Math.round(ns.reduce((s, n) => s + n, 0) * 100) / 100;

/** R3: the page currency, else the one currency the month's approved plans use, else null. */
const currencyFor = (currency: string | null, plans: readonly PlanRow[], month: string): string | null => {
  if (currency) return currency;
  const used = [...new Set(plans.filter((p) => p.status === 'approved' && p.month === month).map((p) => p.currency))];
  return used.length === 1 ? used[0] : null;
};

const planCount = (plans: readonly PlanRow[], month: string): number =>
  new Set(plans.filter((p) => p.status === 'approved' && p.month === month).map((p) => p.currency)).size;

/**
 * Days of DATA: the latest data day in this currency, so a lagging sync is not
 * read as underspend. With no data at all, day 1 through yesterday; on the 1st
 * yesterday is last month, so 0 days (too early), never a whole month of nothing.
 */
const daysOf = (today: string, month: string, dataThrough: string | null): number => {
  if (dataThrough) return dayOfMonth(dataThrough);
  const yesterday = shiftDataDate(today, -1);
  return yesterday < month ? 0 : dayOfMonth(yesterday);
};

const latest = (rows: readonly MonthSpendRow[]): string | null =>
  rows.map((r) => r.dataThrough).filter((d): d is string => d !== null).sort().pop() ?? null;

/**
 * Per-channel lines, only when any channel plan exists. Every channel uses the
 * currency's days (spec §4: days is per currency), in TARGET_PLATFORMS order.
 */
const channelsOf = (
  plans: readonly PlanRow[], month: string, currency: string, rows: readonly MonthSpendRow[], days: number, daysInMonth: number,
): BudgetPacingChannel[] => {
  const anyChannelPlan = plans.some((p) => p.status === 'approved' && p.month === month && p.currency === currency && p.platform !== null);
  if (!anyChannelPlan) return [];
  return TARGET_PLATFORMS.flatMap((platform: TargetPlatform) => {
    const plan = planFor(plans, month, currency, platform)?.amount ?? null;
    const own = rows.filter((r) => r.platform === platform);
    if (plan === null && own.length === 0) return [];
    const spend = sum(own.map((r) => r.spend));
    return [{ platform, plan, spend, pacing: plan === null ? null : pacingOf({ spend, plan, days, daysInMonth }) }];
  });
};

/**
 * A currency-keyed noPlan names its currency, as the box does ("no plan for THB"),
 * so the CSV and the chat rows agree with it and with budgetPacing.currency (D1).
 */
const noPlanTable = (label: LabelFn, reason: 'none' | 'pickCurrency', currency: string | null) => {
  if (reason === 'pickCurrency') return tableOf(label, ['item', 'value'], [{ item: label('status'), value: label('pacingPickCurrency') }]);
  if (currency === null) return tableOf(label, ['item', 'value'], [{ item: label('status'), value: label('pacingNoPlan') }]);
  return tableOf(label, ['item', 'value'], [
    { item: label('status'), value: label('pacingNoPlanInCurrency') },
    { item: label('currency'), value: currency },
  ]);
};

const readyTable = (label: LabelFn, v: Extract<BudgetPacingView, { kind: 'ready' }>) => {
  const rows: Array<[string, Cell]> = [
    [label('month'), v.month],
    [label('status'), label(STATUS_WORD[v.pacing.status])],
    [label('pacingPct'), numCell(v.pacing.pct)],
    [label('currency'), v.currency],
    [label('spend'), numCell(v.spend)],
    [label('plan'), numCell(v.plan)],
    [label('projected'), numCell(shownProjection(v.pacing))],
    [label('dataThrough'), v.dataThrough ?? label('pacingNoDataYet')],
    [label('pacingDays'), String(v.days)],
    ...(v.planDerived ? [[label('note'), label('pacingDerived')] as [string, Cell]] : []),
    ...(v.mismatch ? [[label('channelSum'), numCell(v.mismatch.channelSum)] as [string, Cell]] : []),
    ...(v.unknownCurrencyExcluded ? [[label('note'), label('pacingUnknownCurrency')] as [string, Cell]] : []),
    ...v.channels.map((c): [string, Cell] => [platformLabel(c.platform), numCell(c.spend)]),
  ];
  // A value it withholds (the first days' pacing and projection) is the box's "—" in the file; with no data date yet,
  // the cell says the box's own sentence (rules-04 D re-review LOW 2).
  return tableOf(label, ['item', 'value'], rows.map(([item, value]) => ({ item, value })), ['value']);
};

const noPlan = (month: string, currency: string | null, reason: 'none' | 'pickCurrency', label: LabelFn): AppTileData => ({
  widget: 'budgetPacing', view: { kind: 'noPlan', month, currency, reason }, table: noPlanTable(label, reason, currency), hidden: false,
});

export const budgetPacingOf = (a: {
  today: string; currency: string | null; plans: readonly PlanRow[]; spend: readonly MonthSpendRow[]; label: LabelFn;
}): AppTileData => {
  const month = monthStartOf(a.today);
  const currency = currencyFor(a.currency, a.plans, month);
  if (currency === null) return noPlan(month, null, planCount(a.plans, month) > 1 ? 'pickCurrency' : 'none', a.label);
  const total = planFor(a.plans, month, currency);
  if (total === null) return noPlan(month, currency, 'none', a.label);
  const rows = a.spend.filter((r) => r.currency === currency);
  const daysInMonth = daysInMonthOf(month);
  const dataThrough = latest(rows);
  const days = daysOf(a.today, month, dataThrough);
  const spend = sum(rows.map((r) => r.spend));
  const view: Extract<BudgetPacingView, { kind: 'ready' }> = {
    kind: 'ready', month, currency, plan: total.amount, planDerived: total.derived, mismatch: total.mismatch,
    spend, days, daysInMonth, dataThrough,
    pacing: pacingOf({ spend, plan: total.amount, days, daysInMonth }),
    channels: channelsOf(a.plans, month, currency, rows, days, daysInMonth),
    unknownCurrencyExcluded: a.spend.some((r) => r.currency === null),
  };
  return { widget: 'budgetPacing', view, table: readyTable(a.label, view), hidden: false };
};

export const budgetPacingChatOf = (v: BudgetPacingView): BudgetPacingChat => {
  if (v.kind === 'noPlan') return { status: 'noPlan', reason: v.reason, currency: v.currency };
  return {
    month: v.month, currency: v.currency, spend: v.spend, plan: v.plan, planDerived: v.planDerived,
    pacingPct: v.pacing.pct, status: v.pacing.status, projected: shownProjection(v.pacing),
    dataThrough: v.dataThrough, days: v.days, daysInMonth: v.daysInMonth, unknownCurrencyExcluded: v.unknownCurrencyExcluded,
    channels: v.channels.map((c) => ({
      platform: c.platform, plan: c.plan, spend: c.spend, pacingPct: c.pacing?.pct ?? null, status: c.pacing?.status ?? null,
    })),
  };
};
